/**
 * useFreshActiveDriveFolder
 *
 * Listens for `xavior:active-drive-folder-changed` CustomEvents and maintains
 * BOTH React state (for re-renders) and a ref updated synchronously in the
 * handler (so send callbacks read the freshest value before React flushes).
 * Mirrors useFreshActivePageContext — see that file for the staleness-race
 * rationale. DriveModule dispatches the event with `{ id, label }` for a real
 * folder, or `null` when at root / a non-module folder.
 */
import { useEffect, useRef, useState } from 'react';

export type ActiveDriveFolder = { id: string; label: string };

export type UseFreshActiveDriveFolderResult = {
  state: ActiveDriveFolder | null;
  ref: React.MutableRefObject<ActiveDriveFolder | null>;
};

export function useFreshActiveDriveFolder(): UseFreshActiveDriveFolderResult {
  const [state, setState] = useState<ActiveDriveFolder | null>(null);
  const ref = useRef<ActiveDriveFolder | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ActiveDriveFolder | null;
      const next = detail && detail.id ? { id: detail.id, label: detail.label } : null;
      ref.current = next;
      setState(next);
    };
    window.addEventListener('xavior:active-drive-folder-changed', handler);
    return () => window.removeEventListener('xavior:active-drive-folder-changed', handler);
  }, []);

  return { state, ref };
}
